import { ReactNode, useEffect, useId, useRef, useState } from 'react';
import { type FloatingContext, useInteractions, useListNavigation } from '@floating-ui/react';

import { Icon } from '../icon';
import { NoData } from '../no-data';
import { Input, type InputProps } from '../input';
import { InlineConfirmation } from '../inline-confirmation';
import { type ComboboxData, ComboboxOption } from '../combobox-option';

import EmptySvg from './assets/empty.svg?react';

import styles from './combobox.module.scss';

export const Combobox = (props: ComboboxProps) => {
  const {
    data,
    search,
    addNew,
    context,
    selections,
    exclusions,
    onConfirm,
    inputProps,
    onSearchChange,
    showConfirmation,
    noDataActionLabel,
    onConfirmationOpen,
    onSelectionChange,
    onExclusionChange,
    onConfirmationClose,
    placeholder = 'Search...',
  } = props;

  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const listBoxId = useId();
  const listRef = useRef<Array<HTMLElement | null>>([]);

  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    loop: true,
    virtual: true,
    orientation: 'vertical',
    focusItemOnOpen: false,
    onNavigate: setActiveIndex,
  });

  const { getReferenceProps, getItemProps } = useInteractions([listNav]);

  const toggleList = (list: string[], id: string) => (list.includes(id) ? list.filter((i) => i !== id) : [...list, id]);

  const handleSelectionChange = (item: ComboboxData) => {
    onSelectionChange(toggleList(selections, item.id));
    onExclusionChange?.(exclusions?.filter((id) => id !== item.id) ?? []);
  };

  const handleExclusionChange = (item: ComboboxData) => {
    onExclusionChange?.(toggleList(exclusions ?? [], item.id));
    onSelectionChange(selections.filter((id) => id !== item.id));
  };

  useEffect(() => {
    // Keep activeIndex valid if data changes
    if (activeIndex != null && activeIndex >= data.length) {
      setActiveIndex(data.length ? data.length - 1 : null);
    }
  }, [data, activeIndex]);

  const selectActive = () => {
    const item = activeIndex !== null && data[activeIndex];
    item && handleSelectionChange(item);
  };

  return (
    <>
      <InlineConfirmation
        offset={[0, 10]}
        onConfirm={onConfirm}
        closeOnConfirm={false}
        open={!!showConfirmation}
        error={!search.trim().length}
        onClose={onConfirmationClose}
      >
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <Input
            autoFocus
            value={search}
            clearable={false}
            size="extra-large"
            appearance="underline"
            placeholder={placeholder}
            onChange={(e) => onSearchChange?.(e.target.value)}
            left={<Icon type="search-01" size={20} />}
            {...inputProps}
            {...getReferenceProps({
              role: 'combobox',
              'aria-expanded': true,
              'aria-controls': listBoxId,
              'aria-activedescendant': activeIndex != null ? `${listBoxId}-opt-${activeIndex}` : undefined,
              onKeyDown: (e) => {
                // prevent caret move when navigating through items
                if (['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) {
                  e.preventDefault();
                }

                e.key === 'Enter' && selectActive();
                e.key === 'Escape' && onConfirmationClose?.();

                inputProps?.onKeyDown?.(e as any);
              },
              onChange: (e: any) => onSearchChange?.(e.target.value),
            })}
          />
          {addNew}
        </div>
      </InlineConfirmation>
      {!data.length ? (
        <NoData
          size="small"
          query={search}
          icon={<EmptySvg />}
          description={!search ? 'And then there were none...' : 'No results for'}
          actions={
            !!noDataActionLabel && !!onConfirmationOpen
              ? [
                  {
                    size: 'small',
                    color: 'light',
                    label: noDataActionLabel,
                    onClick: onConfirmationOpen,
                  },
                ]
              : []
          }
        />
      ) : (
        <div className={styles.comboboxContent}>
          {data.map((item, index) => (
            <ComboboxOption
              key={item.id}
              id={`${listBoxId}-opt-${index}`}
              ref={(el) => {
                listRef.current[index] = el;
              }}
              data={item}
              excluded={exclusions?.includes(item.id)}
              showExclude={Array.isArray(exclusions)}
              selected={selections.includes(item.id)}
              highlighted={index === activeIndex}
              onMouseDown={(e) => e.preventDefault()}
              onExclude={() => handleExclusionChange(item)}
              {...getItemProps({
                onMouseMove: () => setActiveIndex(index),
                onClick: () => handleSelectionChange(item),
              })}
            />
          ))}
        </div>
      )}
    </>
  );
};

export interface ComboboxProps {
  search: string;
  addNew?: ReactNode;
  selections: string[];
  exclusions?: string[];
  placeholder?: string;
  data: ComboboxData[];
  onConfirm?: () => void;
  context: FloatingContext;
  showConfirmation?: boolean;
  noDataActionLabel?: string;
  onConfirmationOpen?: () => void;
  onConfirmationClose?: () => void;
  inputProps?: Partial<InputProps>;
  onSearchChange?: (value: string) => void;
  onSelectionChange: (item: string[]) => void;
  onExclusionChange?: (ids: string[]) => void;
}
